import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { MoreMenu } from "@/components/MoreMenu";
import { FitToWidth } from "@/components/FitToWidth";
import { OperatorSelect } from "@/components/Employees";
import { ArrowLeft, Printer, Save, Loader2, CheckCircle2, RotateCcw, Plus, X } from "lucide-react";
import { eur, num, money, fix2, STD_MATERIALS, buildMaterials, buildParts, materialEur, partEur, materialsTotal, partsTotal } from "@/lib/consuntivo";
import { MaterialEditor, PartsEditor } from "@/components/ConsuntivoEditors";

const fmt = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "");
const inp = "bg-transparent outline-none focus:bg-amber-50 print:bg-transparent";

// Office only (not printed): quote vs final, positive = earned vs quote
const Margin = ({ quote, net, closed }) => {
  if (!quote) return <p className="no-print mt-3 text-[11px] text-neutral-500" data-testid="consuntivo-margin-empty">Inserisci il preventivo nella scheda per vedere il margine.</p>;
  const m = Math.round((quote - net) * 100) / 100;
  const good = m >= 0;
  return (
    <div className={`no-print ml-auto w-[95mm] mt-3 text-sm border-2 ${good ? "border-green-600" : "border-red-600"}`} data-testid="consuntivo-margin">
      <div className="flex justify-between px-2 py-1 border-b border-neutral-300"><span>Preventivo (+IVA)</span><b data-testid="consuntivo-margin-quote">{eur(quote)}</b></div>
      <div className="flex justify-between px-2 py-1 border-b border-neutral-300"><span>Consuntivo (imponibile)</span><b>{eur(net)}</b></div>
      <div className={`flex justify-between px-2 py-1.5 font-bold ${good ? "bg-green-50 text-green-800" : "bg-red-50 text-red-700"}`}>
        <span>{good ? (closed ? "Guadagnato rispetto al preventivo" : "Margine attuale") : "Oltre il preventivo"}</span>
        <span data-testid="consuntivo-margin-value">{good ? "+" : "−"}{eur(Math.abs(m))} ({Math.round((Math.abs(m) / quote) * 100)}%)</span>
      </div>
    </div>
  );
};


const Head = ({ job, closed }) => (
  <div className="flex items-start justify-between border-b-2 border-neutral-900 pb-2 mb-4">
    <div>
      <img src="/logo.png" alt="Centro Auto Pontedera" className="h-9 w-auto" />
      <div className="text-[10px] text-neutral-600 mt-1">Carrozzeria · Via Don Primo Mazzolari 34/38, 56025 Pontedera (PI) · 0587 484791</div>
    </div>
    <div className="text-right">
      <div className="text-lg font-extrabold uppercase">Consuntivo lavori</div>
      <div className="text-xs">Scheda n. <b>{job.number || "—"}</b> · ingresso {fmt(job.date_in)}</div>
      <div className="text-xs" data-testid="consuntivo-status">{closed ? <>Fine lavoro: <b>{fmt(job.closed_at)}</b></> : <span className="text-[#E63946] font-bold">Lavorazione in corso</span>}</div>
    </div>
  </div>
);

const Info = ({ job }) => (
  <div className="grid grid-cols-4 gap-x-4 gap-y-1 text-xs mb-4 border border-neutral-400 p-2">
    {[["Cliente", job.customer_name], ["Telefono", job.customer_phone], ["Veicolo", `${job.brand} ${job.model}`], ["Targa", job.plate],
      ["Telaio", job.vin], ["Colore", job.color], ["Km", job.km], ["Consegna prevista", fmt(job.date_out)]].map(([k, v]) => (
      <div key={k}><div className="text-[8px] uppercase tracking-wider text-neutral-500 font-bold">{k}</div><div className="font-bold">{v || "—"}</div></div>
    ))}
  </div>
);

const Title = ({ children }) => <div className="text-[11px] font-extrabold uppercase tracking-[0.12em] bg-neutral-900 text-white px-2 py-0.5 mt-4 mb-1">{children}</div>;

const Hours = ({ entries, locked, onEdit }) => {
  const total = entries.reduce((a, e) => a + num(e.hours), 0);
  const ops = {};
  entries.forEach((e) => { ops[e.operator] = (ops[e.operator] || 0) + num(e.hours); });
  return (
    <table className="w-full text-xs" data-testid="consuntivo-hours">
      <thead><tr className="text-left border-b border-neutral-400"><th className="py-1 w-20">Data</th><th className="w-32">Operatore</th><th>Lavori eseguiti</th><th className="text-right w-16">Ore</th></tr></thead>
      <tbody>
        {entries.length === 0 && <tr><td colSpan={4} className="py-2 text-neutral-500">Nessuna scheda ore caricata per questa targa.</td></tr>}
        {entries.map((e, i) => (
          <tr key={i} className="border-b border-neutral-200" data-testid={`consuntivo-entry-${i}`}>
            <td className="py-0.5">{fmt(e.date)}</td>
            <td>{locked ? e.operator : <OperatorSelect value={e.operator} onChange={(v) => onEdit(i, "operator", v)} className={`w-full ${inp}`} testid={`entry-${i}-operator`} />}</td>
            <td>{locked ? e.works : <input value={e.works} onChange={(ev) => onEdit(i, "works", ev.target.value)} className={`w-full ${inp}`} data-testid={`entry-${i}-works`} />}</td>
            <td className="text-right">{locked ? num(e.hours).toLocaleString("it-IT") : <input value={e.hours} onChange={(ev) => onEdit(i, "hours", ev.target.value)} inputMode="decimal" className={`w-14 text-right ${inp}`} data-testid={`entry-${i}-hours`} />}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        {Object.entries(ops).map(([n, h]) => <tr key={n} className="text-neutral-600"><td /><td colSpan={2}>Totale {n}</td><td className="text-right">{h.toLocaleString("it-IT")}</td></tr>)}
        <tr className="font-bold border-t border-neutral-900"><td /><td colSpan={2}>Totale ore</td><td className="text-right" data-testid="consuntivo-total-hours">{(Math.round(total * 100) / 100).toLocaleString("it-IT")}</td></tr>
      </tfoot>
    </table>
  );
};

// iPhone: big finger-friendly fields above the (zoomed) A4 sheet, same state as the sheet
const big = "w-full h-12 rounded-xl bg-[#12141A] border border-white/15 px-3 text-base text-white";
const MobileEdit = ({ entries, onEdit, f, setF, setMaterials, setParts }) => (
  <div className="no-print sm:hidden px-3 pt-4 space-y-4" data-testid="consuntivo-mobile-edit">
    <section>
      <h3 className="font-display font-bold uppercase text-sm mb-2 text-white">Ore operatori</h3>
      {entries.length === 0 && <p className="text-sm text-muted-foreground">Nessuna ora caricata.</p>}
      <div className="space-y-2">
        {entries.map((e, i) => (
          <div key={i} className="rounded-2xl border border-white/10 bg-[#181A22] p-3 space-y-2" data-testid={`m-entry-${i}`}>
            <div className="text-xs text-muted-foreground">{fmt(e.date)}</div>
            <div className="grid grid-cols-[1fr_90px] gap-2">
              <OperatorSelect value={e.operator} onChange={(v) => onEdit(i, "operator", v)} className={big} testid={`m-entry-${i}-operator`} />
              <input value={e.hours} onChange={(ev) => onEdit(i, "hours", ev.target.value)} inputMode="decimal" className={`${big} text-right font-bold`} aria-label="Ore" data-testid={`m-entry-${i}-hours`} />
            </div>
            <input value={e.works} onChange={(ev) => onEdit(i, "works", ev.target.value)} placeholder="Lavoro eseguito" className={big} data-testid={`m-entry-${i}-works`} />
          </div>
        ))}
      </div>
    </section>
    <section>
      <h3 className="font-display font-bold uppercase text-sm mb-2 text-white">Materiali</h3>
      <MaterialEditor rows={f.materials} set={setMaterials} testid="m-material" />
    </section>
    <section>
      <h3 className="font-display font-bold uppercase text-sm mb-2 text-white">Ricambi</h3>
      <PartsEditor rows={f.parts} set={setParts} testid="m-part" />
    </section>
    <section>
      <label className="text-xs text-muted-foreground">Tariffa €/ora<input value={f.hourly_rate} onChange={(ev) => setF({ ...f, hourly_rate: ev.target.value })} inputMode="decimal" className={`${big} mt-1 text-right font-bold`} data-testid="m-rate" /></label>
    </section>
    <p className="text-xs text-muted-foreground">Le modifiche si vedono subito nel foglio qui sotto. Tocca <b>Salva</b> in alto per salvarle.</p>
  </div>
);

const Materials = ({ rows, onChange, locked }) => (
  <div data-testid="consuntivo-materials">
    {rows.map((m, i) => (
      <div key={i} className="flex gap-2 border-b border-neutral-200 text-xs py-0.5 items-center">
        <span className="w-5 text-neutral-400">{i + 1}.</span>
        <input value={m.description} disabled={locked} onChange={(e) => onChange(rows.map((r, j) => (j === i ? { ...r, description: e.target.value, edited: true } : r)))} placeholder="Descrizione materiale" className={`flex-1 ${inp}`} data-testid={`material-${i}-desc`} />
        <span className={`text-neutral-500 ${m.qty ? "" : "print:hidden"}`}>Q.tà</span>
        <input value={m.qty} disabled={locked} onChange={(e) => onChange(rows.map((r, j) => (j === i ? { ...r, qty: e.target.value, edited: true } : r)))} className={`w-28 text-right ${inp}`} data-testid={`material-${i}-qty`} />
        <span className="text-neutral-500">€</span>
        <input value={m.price ?? ""} disabled={locked} onBlur={() => m.price && onChange(rows.map((r, j) => (j === i ? { ...r, price: fix2(r.price) } : r)))} onChange={(e) => onChange(rows.map((r, j) => (j === i ? { ...r, price: e.target.value, edited: true } : r)))} inputMode="decimal" placeholder={materialEur(m) ? String(materialEur(m)).replace(".", ",") : "0,00"} className={`w-16 text-right font-bold ${inp}`} data-testid={`material-${i}-price`} />
        {!locked && <button onClick={() => onChange(rows.filter((_, j) => j !== i))} className="no-print text-neutral-400 hover:text-[#E63946]" aria-label="Togli" data-testid={`material-${i}-remove`}><X className="w-3.5 h-3.5" /></button>}
      </div>
    ))}
    {!locked && (
      <div className="no-print flex flex-wrap items-center gap-1.5 mt-1.5">
        <button onClick={() => onChange([...rows, { description: "", qty: "", price: "", edited: true }])} className="text-xs text-[#3B82F6] flex items-center gap-1 mr-2" data-testid="material-add"><Plus className="w-3.5 h-3.5" />Aggiungi materiale</button>
        {STD_MATERIALS.filter((s) => !rows.some((r) => r.description.trim().toLowerCase() === s.toLowerCase())).map((s) => (
          <button key={s} onClick={() => onChange([...rows, { description: s, qty: "", price: "", edited: true }])} className="text-[11px] rounded-full border border-neutral-300 px-2 py-0.5 hover:bg-neutral-100" data-testid={`material-quick-${s.toLowerCase()}`}>+ {s}</button>
        ))}
      </div>
    )}
  </div>
);

const Parts = ({ rows, onChange, locked }) => {
  const set = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <table className="w-full text-xs" data-testid="consuntivo-parts">
      <thead><tr className="text-left border-b border-neutral-400"><th className="py-1">Ricambio</th><th className="text-right w-14">Q.tà</th><th className="text-right w-24">Prezzo cad.</th><th className="text-right w-24">Totale</th><th className="w-4 no-print" /></tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={5} className="py-2 text-neutral-500">Nessun ricambio.</td></tr>}
        {rows.map((p, i) => (
          <tr key={i} className="border-b border-neutral-200" data-testid={`part-${i}`}>
            <td className="py-0.5">{p.fromSheet || locked ? p.description : <input value={p.description} onChange={(e) => set(i, { description: e.target.value })} placeholder="Descrizione ricambio" className={`w-full ${inp}`} data-testid={`part-${i}-desc`} />}</td>
            <td className="text-right"><input value={p.qty} disabled={locked} onChange={(e) => set(i, { qty: e.target.value })} inputMode="decimal" className={`w-12 text-right ${inp}`} data-testid={`part-${i}-qty`} /></td>
            <td className="text-right">€ <input value={p.price ?? ""} disabled={locked} onBlur={() => p.price && set(i, { price: fix2(p.price) })} onChange={(e) => set(i, { price: e.target.value })} inputMode="decimal" placeholder="0,00" className={`w-16 text-right font-bold ${inp}`} data-testid={`part-${i}-price`} /></td>
            <td className="text-right font-bold" data-testid={`part-${i}-total`}>{eur(partEur(p))}</td>
            <td className="no-print text-right">{!locked && !p.fromSheet && <button onClick={() => onChange(rows.filter((_, j) => j !== i))} className="text-neutral-400 hover:text-[#E63946]" aria-label="Togli" data-testid={`part-${i}-remove`}><X className="w-3.5 h-3.5" /></button>}</td>
          </tr>
        ))}
      </tbody>
      {!locked && <tfoot className="no-print"><tr><td colSpan={5}><button onClick={() => onChange([...rows, { description: "", qty: "1", price: "" }])} className="text-xs text-[#3B82F6] flex items-center gap-1 mt-1.5" data-testid="part-add"><Plus className="w-3.5 h-3.5" />Aggiungi ricambio</button></td></tr></tfoot>}
    </table>
  );
};

export default function ConsuntivoPage() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [entries, setEntries] = useState([]);
  const editEntry = (i, k, v) => setEntries((es) => es.map((e, j) => (j === i || (k === "operator" && e.ts_id === es[i].ts_id) ? { ...e, [k]: v } : e)));
  const load = (data) => {
    setD(data);
    const materials = buildMaterials(data);
    setEntries(data.hours.entries.map((e) => ({ ...e, hours: String(e.hours).replace(".", ","), orig: e })));
    setF({ ...data.final, materials, parts: buildParts(data), hourly_rate: String(data.final.hourly_rate).replace(".", ",") });
  };
  useEffect(() => { api.get(`/admin/bodyshop/${id}/consuntivo`).then((r) => load(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail))); }, [id]);

  if (!d) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#E63946]" /></div>;
  const { job, hours } = d;
  const closed = job.status === "closed";
  const rate = num(f.hourly_rate);
  const totalHours = closed ? hours.total : entries.reduce((a, e) => a + num(e.hours), 0);
  const labour = Math.round(totalHours * rate * 100) / 100;
  const mat = closed ? d.final.materials_amount || 0 : materialsTotal(f.materials);
  const partsAmt = closed ? d.final.parts_amount || 0 : partsTotal(f.parts);
  const net = labour + mat + partsAmt;
  const vat = Math.round(net * 22) / 100;
  const body = () => ({ hourly_rate: rate, materials: f.materials, materials_amount: mat, parts: f.parts, parts_amount: partsAmt, notes: f.notes });
  const setMaterials = (m) => setF((p) => ({ ...p, materials: m }));
  const setParts = (parts) => setF((p) => ({ ...p, parts }));
  const act = async (kind) => {
    if (kind === "close" && !window.confirm("Confermi la fine lavoro? Le ore verranno bloccate su questo consuntivo.")) return;
    setBusy(true);
    try {
      const fixes = entries.filter((e) => e.operator !== e.orig.operator || e.works !== e.orig.works || num(e.hours) !== e.orig.hours);
      if (kind !== "reopen" && fixes.length) {
        if (fixes.some((e) => !e.operator.trim())) throw new Error("Il nome dell'operatore non può essere vuoto");
        await api.post("/admin/timesheets/fix-entries", { entries: fixes.map((e) => ({ ts_id: e.ts_id, row: e.row, operator: e.operator, works: e.works, hours: num(e.hours), plate: job.plate })) });
      }
      const req = kind === "save" ? api.put(`/admin/bodyshop/${id}/consuntivo`, body()) : kind === "close" ? api.post(`/admin/bodyshop/${id}/close`, body()) : api.post(`/admin/bodyshop/${id}/reopen`);
      load((await req).data);
      toast.success(kind === "save" ? "Consuntivo salvato" : kind === "close" ? "Lavorazione chiusa" : "Lavorazione riaperta");
    } catch (e) { toast.error(e.response ? formatApiErrorDetail(e.response.data?.detail) : e.message); }
    finally { setBusy(false); }
  };
  const B = ({ k, icon: I, children, cls = "border-white/15 bg-transparent", variant = "outline" }) => (
    <Button size="sm" variant={variant} onClick={() => act(k)} disabled={busy} className={`rounded-full ${cls}`} data-testid={`consuntivo-${k}-btn`}><I className="w-4 h-4 mr-1.5" />{children}</Button>
  );

  return (
    <div className="min-h-screen bg-[#0B0C10]">
      <header className="no-print sticky top-0 z-30 bg-[#12141A]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex flex-wrap items-center gap-2">
          <Button asChild variant="outline" size="sm" className="rounded-full border-white/15 bg-transparent" data-testid="consuntivo-back-btn">
            <Link to="/admin?tab=bodyshop"><ArrowLeft className="w-4 h-4 mr-1.5" />Indietro</Link>
          </Button>
          <div className="font-display font-bold uppercase text-sm mr-auto">Consuntivo {job.plate}</div>
          {!closed ? <B k="save" icon={Save} variant="default" cls="bg-[#E63946] hover:bg-[#D62828] text-white px-5">Salva</B>
            : <span className="text-xs font-bold uppercase bg-[#16A34A]/20 text-[#4ADE80] rounded-full px-3 py-1.5" data-testid="consuntivo-closed-badge">Lavoro finito</span>}
          <MoreMenu testid="consuntivo-more" items={[
            !closed && { label: "Fine lavoro (blocca le ore)", icon: CheckCircle2, onClick: () => act("close"), testid: "consuntivo-close-btn" },
            closed && { label: "Riapri lavoro", icon: RotateCcw, onClick: () => act("reopen"), testid: "consuntivo-reopen-btn" },
            { label: "Stampa consuntivo", icon: Printer, onClick: () => window.print(), testid: "consuntivo-print-btn" },
          ]} />
        </div>
      </header>
      <p className="no-print max-w-5xl mx-auto px-4 pt-4 text-xs text-muted-foreground">Le ore e i materiali arrivano dalle schede operatore caricate per la targa {job.plate}. Tariffa, materiali e importo si scrivono direttamente sul foglio. Con "Fine lavoro" le ore vengono bloccate.</p>
      {!closed && <MobileEdit entries={entries} onEdit={editEntry} f={f} setF={setF} setMaterials={setMaterials} setParts={setParts} />}
      <div className="py-4 px-2">
        <FitToWidth width={210}>
        <div id="contract-sheet" className="bg-white text-black mx-auto w-[210mm] min-h-[297mm] p-[12mm] shadow-2xl" data-testid="consuntivo-sheet">
          <Head job={job} closed={closed} />
          <Info job={job} />
          {job.works && <><Title>Lavori richiesti</Title><div className="text-xs whitespace-pre-line">{job.works}</div></>}
          <Title>Manodopera</Title>
          <Hours entries={entries} locked={closed} onEdit={editEntry} />
          <Title>Materiali</Title>
          <Materials rows={f.materials} locked={closed} onChange={setMaterials} />
          {!closed && hours.materials_unpriced?.length > 0 && <p className="no-print text-[11px] text-amber-700 mt-1" data-testid="consuntivo-unpriced">Senza prezzo: {hours.materials_unpriced.join(", ")}. Scrivi il prezzo nella colonna €.</p>}
          <Title>Ricambi</Title>
          <Parts rows={f.parts} locked={closed} onChange={setParts} />
          {f.notes || !closed ? <><Title>Note</Title><textarea value={f.notes} disabled={closed} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} className={`w-full text-xs resize-none ${inp}`} data-testid="consuntivo-notes" /></> : null}
          <div className="ml-auto w-[95mm] mt-5 text-sm border-2 border-neutral-900" data-testid="consuntivo-totals">
            <div className="flex justify-between px-2 py-1 border-b border-neutral-300"><span>Manodopera {(Math.round(totalHours * 100) / 100).toLocaleString("it-IT")} h × <input value={f.hourly_rate} disabled={closed} onChange={(e) => setF({ ...f, hourly_rate: e.target.value })} inputMode="decimal" className={`w-12 text-right font-bold ${inp}`} data-testid="consuntivo-rate" /> €/h</span><b data-testid="consuntivo-labour">{eur(labour)}</b></div>
            <div className="flex justify-between px-2 py-1 border-b border-neutral-300"><span>Materiali</span><b data-testid="consuntivo-materials-amount">{eur(mat)}</b></div>
            <div className="flex justify-between px-2 py-1 border-b border-neutral-300"><span>Ricambi</span><b data-testid="consuntivo-parts-amount">{eur(partsAmt)}</b></div>
            <div className="flex justify-between px-2 py-1 border-b border-neutral-300"><span>Imponibile</span><b data-testid="consuntivo-net">{eur(net)}</b></div>
            <div className="flex justify-between px-2 py-1 border-b border-neutral-300"><span>IVA 22%</span><b data-testid="consuntivo-vat">{eur(vat)}</b></div>
            <div className="flex justify-between px-2 py-1.5 bg-neutral-900 text-white text-base"><span className="font-bold">TOTALE</span><b data-testid="consuntivo-total">{eur(net + vat)}</b></div>
          </div>
          <Margin quote={money(job.quote_total)} net={net} closed={closed} />
          <div className="mt-10 grid grid-cols-2 gap-10 text-[10px] text-neutral-600">
            <div className="border-t border-neutral-500 pt-1">Firma Centro Auto Pontedera</div>
            <div className="border-t border-neutral-500 pt-1">Firma cliente per accettazione</div>
          </div>
        </div>
        </FitToWidth>
      </div>
    </div>
  );
}
